import { Chip, IconButton } from '@material-tailwind/react';
import CircularProgress from '@mui/material/CircularProgress';
import { KanbanCard, KanbanType } from '../../config/kanban';
import { ColorPicker } from '../../config/ColorPicker';
import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import appService from '../../service/app.service';
import { getIcon, getInitials } from '../../utils/helper';
import useSimpleTaskDeadLineNotify from '../../context/SimpleTaskDeadLineNotifyContext';
import { Avatar, AvatarGroup } from '@mui/material';

const Field = ({ label, value, bgColor, isChip, dateType, isAvatar, maxAvatar, data, includeRespPersonList }) => {
  let formattedValue = value;
  if (dateType && value) {
    const d = dayjs(value);
    if (d.isValid()) {
      switch (dateType) {
        case 'date':
          formattedValue = d.format('YYYY-MM-DD');
          break;
        case 'datetime':
          formattedValue = d.format('YYYY-MM-DD HH:mm:ss');
          break;
        case 'time':
          formattedValue = d.format('HH:mm:ss');
          break;
        default:
          formattedValue = value;
      }
    } else {
      formattedValue = 'N/A';
    }
  }
  if (isChip) {
    return (
      <div className={`flex items-center gap-x-2 flex-wrap`}>
        <p className="font-medium">{label}</p>
        {formattedValue && (
          <Chip
            value={formattedValue}
            className="rounded-full truncate block"
            variant="ghost"
            style={{ backgroundColor: bgColor }}
          />
        )}
      </div>
    );
  } else if (isAvatar) {
    return (
      <div className="flex items-center flex-wrap w-[100%]">
        <p className="font-medium">{label}</p>
        <AvatarGroup
          max={maxAvatar}
          sx={{
            width: 'fit-content',
            '& .MuiAvatar-root': {
              width: 30,
              height: 30,
              fontSize: '1rem',
            },
          }}
        >
          {formattedValue && (
            <Avatar sx={{ bgcolor: '#1185f2', width: 30, height: 30, fontSize: '1rem' }}>
              {getInitials(formattedValue)}
            </Avatar>
          )}
          {includeRespPersonList &&
            data?.respPersonlist?.map((rp) => (
              <Avatar key={rp.userId} sx={{ bgcolor: '#F9BC04', width: 30, height: 30, fontSize: '1rem' }}>
                {getInitials(rp?.user?.nickName)}
              </Avatar>
            ))}
        </AvatarGroup>
      </div>
    );
  } else if (includeRespPersonList) {
    return (
      <div className="truncate">
        <p className="font-medium">{label}</p>
        {[data?.responsiblePerson?.userName, ...data?.respPersonlist?.map((rp) => rp.user.userName)]
          .filter(Boolean)
          .join(', ')}
      </div>
    );
  } else {
    return (
      <p className="mb-1">
        <span className="font-medium">{label}</span> {formattedValue}
      </p>
    );
  }
};

const Card = ({
  data,
  onDragStart,
  statusColor,
  secondaryColor,
  loading,
  type,
  params,
  user,
  setRefetch,
  handleClick,
  slideCount,
  getItemById,
  setReportDialogOpen,
}) => {
  const columnRef = useRef(null);
  const [maxAvatar, setMaxAvatar] = useState(null);
  const { simpleTaskDeadLineNotify } = useSimpleTaskDeadLineNotify();
  const [cardParams, setCardParams] = useState(null);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const width = entry.contentRect.width;

        const calculatedMax = Math.floor(width / 28);

        setMaxAvatar(calculatedMax);
      }
    });

    if (columnRef.current) {
      observer.observe(columnRef.current);
    }

    return () => observer.disconnect();
  }, [slideCount]);

  useEffect(() => {
    const kanbanCardConfig = KanbanCard.find((card) => card.type === type);
    const paramName = kanbanCardConfig?.paramName;

    if (params[paramName]) {
      const parsedParams = JSON.parse(params[paramName]);
      setCardParams(parsedParams);
    }
  }, [params, type]);

  const getFieldValue = (obj, path) => {
    return path.split('.').reduce((acc, part) => {
      if (Array.isArray(acc)) {
        return acc.map((item) => item[part]);
      } else {
        return acc ? acc[part] : null;
      }
    }, obj);
  };

  const getColor = (fieldColor, useColorBgCode = false) => {
    if (!fieldColor) return null;
    const color = getFieldValue(data, fieldColor);
    const colorObj = ColorPicker.find((col) => col.colorName === color);
    return colorObj ? (useColorBgCode ? colorObj.colorBgCode : colorObj.colorCode) : color;
  };

  const iAmTheAddress =
    data.messages?.length > 0 &&
    data.messages?.some((message) => {
      return message.addressedTo === user?.userId && message.readAt === null;
    });

  const cannotReadAt = data.messages?.find((message) => message.addressedTo === user?.userId);

  const handleUpdateReadAt = () => {
    const findedItem = data.messages?.find((msg) => msg.lineId === data.id);
    if (iAmTheAddress && cannotReadAt.readAt === null) {
      appService.updateReadAt(findedItem.id).then(() => {
        setRefetch(true);
      });
    }
  };

  const finishedStatusId = Boolean(params.SIMPLETASK_FINISHED_STATUS)
    ? parseInt(params.SIMPLETASK_FINISHED_STATUS)
    : null;

  const getDeadlineColor = () => {
    if (type !== KanbanType.TASK || finishedStatusId === data.statusId || !data.endDate) {
      return null;
    }

    const today = dayjs().startOf('day');
    const end = dayjs(data.endDate).startOf('day');

    if (simpleTaskDeadLineNotify?.length > 0) {
      const diffDays = end.diff(today, 'day');

      const match = simpleTaskDeadLineNotify.find((notify) => diffDays <= parseInt(notify.daysBefore));

      if (match) return match.color;
    }

    return null;
  };

  const deadlineColor = getDeadlineColor();

  const cardBackgroundParam = JSON.parse(params.KANBAN_CARD_BACKGROUND || '{}')[type];

  const handleShowMore = () => {
    handleUpdateReadAt();
    getItemById(data.id);
  };

  return (
    <div
      ref={columnRef}
      key={data.id}
      draggable
      onDragStart={(event) => onDragStart(event, data)}
      className={`relative mb-3 ${loading ? 'relative opacity-40 cursor-not-allowed' : 'cursor-pointer rounded hover:bg-kanbanHoverBackground'}`}
      style={{
        border: '1px solid #e6e6e6',
        borderTop: `7px solid ${statusColor ? statusColor?.colorCode : secondaryColor}`,
        borderRadius: '8px',
        background: iAmTheAddress
          ? '#fecaca'
          : deadlineColor
            ? deadlineColor
            : data.backgroundColor?.startsWith('#')
              ? data.backgroundColor ?? 'white'
              : ColorPicker.find((col) => col.colorName === data.backgroundColor)?.kanbanBgColor ??
                getColor(cardBackgroundParam, true) ??
                'white',
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        handleClick(e, { id: data.id });
      }}
    >
      {data.color && <div className="w-full h-[35px] z-10" style={{ background: data.color }} />}
      {loading && (
        <div className="h-full w-full rounded z-50 left-0 absolute top-0 text-white flex items-center justify-center">
          <CircularProgress size={50} />
        </div>
      )}
      <div className={`grid grid-cols-4 text-[14px] mt-3 px-3 mb-2 font-medium ${data.color && 'mt-12'}`}>
        {cardParams && cardParams.length > 0 && (
          <div className="col-span-3 line-clamp-3">
            <Field
              label={cardParams[0].label}
              value={getFieldValue(data, cardParams[0].fieldName) || 'Nincs megadva.'}
              bgColor={getColor(cardParams[0].color)}
              isChip={cardParams[0].view === 'chip'}
              dateType={cardParams[0].dateType}
            />
          </div>
        )}
        <div className="col-span-1 justify-self-end -mt-0.5">
          {type === KanbanType.TASKLIST ? (
            <IconButton
              onClick={() => {
                setReportDialogOpen((prev) => ({
                  ...prev,
                  open: true,
                  data: {
                    projectId: data.lineId,
                    taskListId: data.id,
                    statusId: data.statusId,
                  },
                }));
                handleUpdateReadAt();
              }}
              variant="gradient"
              color="blue-gray"
            >
              {getIcon('Description', '2.5rem', 'white')}
            </IconButton>
          ) : (
            <IconButton onClick={handleShowMore} className="bg-[#78b8ef]" size="sm">
              <svg className="h-[22px] w-[22px]" viewBox="0 0 256 256" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M251 123.13C250.63 122.32 241.87 102.87 222.52 83.52C196.63 57.67 164 44 128 44C92 44 59.37 57.67 33.51 83.52C14.16 102.87 5.40002 122.32 5.00002 123.13C4.32255 124.666 3.97266 126.326 3.97266 128.005C3.97266 129.684 4.32255 131.344 5.00002 132.88C5.37002 133.7 14.13 153.14 33.49 172.49C59.37 198.34 92 212 128 212C164 212 196.63 198.34 222.48 172.49C241.84 153.14 250.6 133.7 250.97 132.88C251.652 131.346 252.007 129.687 252.012 128.008C252.018 126.329 251.673 124.668 251 123.13ZM204.94 156.13C183.47 177.27 157.59 188 128 188C98.41 188 72.53 177.27 51.09 156.12C42.6535 147.772 35.3964 138.312 29.52 128C35.3981 117.693 42.655 108.235 51.09 99.89C72.54 78.73 98.41 68 128 68C157.59 68 183.46 78.73 204.91 99.89C213.346 108.235 220.603 117.692 226.48 128C220.603 138.311 213.346 147.772 204.91 156.12L204.94 156.13ZM128 84C119.298 84 110.791 86.5806 103.555 91.4153C96.3192 96.2501 90.6796 103.122 87.3493 111.162C84.0191 119.202 83.1477 128.049 84.8455 136.584C86.5432 145.119 90.7338 152.959 96.8873 159.113C103.041 165.266 110.881 169.457 119.416 171.155C127.951 172.852 136.798 171.981 144.838 168.651C152.878 165.32 159.75 159.681 164.585 152.445C169.419 145.209 172 136.702 172 128C171.987 116.335 167.347 105.151 159.098 96.9019C150.849 88.6532 139.665 84.0132 128 84ZM128 148C124.044 148 120.178 146.827 116.889 144.629C113.6 142.432 111.036 139.308 109.522 135.654C108.009 131.999 107.613 127.978 108.384 124.098C109.156 120.219 111.061 116.655 113.858 113.858C116.655 111.061 120.219 109.156 124.098 108.384C127.978 107.613 131.999 108.009 135.654 109.522C139.308 111.036 142.432 113.6 144.629 116.889C146.827 120.178 148 124.044 148 128C148 133.304 145.893 138.391 142.142 142.142C138.391 145.893 133.304 148 128 148Z"
                  fill="white"
                />
              </svg>
            </IconButton>
          )}
        </div>
      </div>
      <div className="text-[14px] text-[#4D4D4D] mb-2 px-3">
        {cardParams &&
          cardParams
            .slice(1)
            .map((item, index) => (
              <Field
                key={index}
                label={item.label}
                value={getFieldValue(data, item.fieldName)}
                bgColor={getColor(item.color)}
                isChip={item.view === 'chip'}
                isAvatar={item.view === 'avatar'}
                includeRespPersonList={item.includeRespPersonList}
                maxAvatar={maxAvatar}
                dateType={item.dateType}
                data={data}
              />
            ))}
      </div>
    </div>
  );
};

export default Card;
